import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Card, Table, Input, Button, Select, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";
import { useState, useEffect, useCallback } from "react";

const { Option } = Select;
const { Title, Text } = Typography;
const { Search } = Input;

const PlanTypeList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();
  // State management
  const [loading, setLoading] = useState(false);
  const [planTypes, setPlanTypes] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} plan types`,
  });

  // Filters
  const [filters, setFilters] = useState({
    searchText: "",
    status: "all",
  });

  const getPlanTypes = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          ...params,
        };

        console.log("Fetching plan types with params:", requestParams);
        const response = await masterService.getPlanTypeList(requestParams);
        console.log("Countries API Response:", response);

        if (response.data && response.data.status) {
          // Handle different possible response structures
          const planTypesData =
            response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          console.log("Plan types data:", planTypesData);
          console.log("Pagination data:", paginationData);

          // Ensure countriesData is an array
          if (Array.isArray(planTypesData)) {
            setPlanTypes(planTypesData);
          } else {
            console.error("Plan types data is not an array:", planTypesData);
            setPlanTypes([]);
          }

          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          notify.error({ message: "Failed to fetch countries" });
          setPlanTypes([]);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
        notify.error({ message: "Failed to fetch countries" });
        setPlanTypes([]);
      } finally {
        setLoading(false);
      }
    },
    [filters.searchText, filters.status]
  );

  // Load countries data
  useEffect(() => {
    setPageTitle("Plan Types");
    getPlanTypes();
  }, [setPageTitle]);

  // Handle search
  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getPlanTypes({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  // Handle status filter change
  const handleStatusChange = (value) => {
    setFilters((prev) => ({ ...prev, status: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getPlanTypes({
      searchText: filters.searchText,
      status: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getPlanTypes({
      searchText: filters.searchText,
      status: filters.status,
      perPage: pageSize,
      currentPage: current,
    });
  };

  // Handle add country
  const handleAddPlanType = () => {
    navigate("/plan-types/add/new");
  };

  // Handle edit country
  const handleEditPlanType = (planTypeId) => {
    navigate(`/plan-types/edit/${planTypeId}`);
  };

  // Table columns
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "order_number",
      dataIndex: "order_number",
      key: "order_number",
      width: 120,
      render: (order_number) => <Tag color={"green"}>{order_number}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (status) => (
        <Tag color={status ? "green" : "red"}>
          {status ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditPlanType(record.id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  // Header content with user info

  return (
    <Card>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Plan Types ({pagination.total})
        </Title>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: 300,
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <Select
            value={filters.status}
            onChange={handleStatusChange}
            placeholder="All"
            style={{ width: 140 }}
          >
            <Option value="all">All</Option>
            <Option value="Y">Active</Option>
            <Option value="N">Inactive</Option>
          </Select>
          <Search
            placeholder="Search plan types..."
            allowClear
            onSearch={handleSearch}
            style={{ width: 320, maxWidth: "40vw" }}
            enterButton={<SearchOutlined />}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddPlanType}
            size="middle"
          >
            Add Plan Type
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={planTypes}
        rowKey="plan_type_id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        size="middle"
        locale={{
          emptyText: "No plan types found",
          filterConfirm: "OK",
          filterReset: "Reset",
          filterEmptyText: "No filters",
        }}
      />
    </Card>
  );
};

export default PlanTypeList;
